import { useMutation, useQuery } from "react-query";
import {
  addCountry,
  addCurrency,
  addDept,
  addLga,
  addStaff,
  addState,
  deleteDept,
  getAccAudits,
  getAllDepts,
  getCountries,
  getCurrencies,
  getDept,
  getLgas,
  getStates,
  getUser,
  updateCountry,
  updateCurrency,
  updateDept,
  updateLga,
  updateState,
} from "../api/account";

const useDataMutation = (mutationFn, mutationKey, options = {}) => {
  const { mutate, isLoading, data } = useMutation(mutationFn, {
    mutationKey,
    ...options,
  });
  return { mutate, isLoading, data };
};

const useDataQuery = (queryFn, queryKey, queryParam = null, options = {}) => {
  const { data, isLoading, isFetching, refetch, isSuccess, isFetched } =
    useQuery(
      queryParam ? [queryKey, queryParam] : [queryKey],
      () => queryFn(...(Array.isArray(queryParam) ? queryParam : [queryParam])),
      {
        enabled: options.enabled ?? !!queryParam,
        ...options,
      },
    );
  return { data, isLoading, refetch, isFetching, isSuccess, isFetched };
};

export const useGetUser = (options) => {
  return useDataQuery(getUser, "getUser", undefined, options);
};

export const useGetDept = (userId, options = {}) =>
  useDataQuery(getDept, "getDept", userId, {
    enabled: !!userId,
    ...options,
  });

export const useGetStates = (
  countryId,
  pageSize,
  pageNumber,
  isSupported,
  searchParam,
  options = {},
) =>
  useDataQuery(
    getStates,
    "getStates",
    [countryId, pageSize, pageNumber, isSupported, searchParam],
    {
      enabled: !!countryId,
      ...options,
    },
  );

export const useGetAccAudits = (pageSize, pageNumber, options = {}) =>
  useDataQuery(getAccAudits, "getAccAudits", [pageSize, pageNumber], {
    ...options,
  });

export const useGetLgas = (
  stateId,
  pageSize,
  pageNumber,
  isSupported,
  searchParam,
  options = {},
) =>
  useDataQuery(
    getLgas,
    "getLgas",
    [stateId, pageSize, pageNumber, isSupported, searchParam],
    {
      enabled: !!stateId,
      ...options,
    },
  );

export const useGetCountries = (
  pageSize,
  pageNumber,
  isSupported,
  searchParam,
  options = {},
) =>
  useDataQuery(
    getCountries,
    "getCountries",
    [pageSize, pageNumber, isSupported, searchParam],
    {
      enabled: !!pageSize && !!pageNumber,
      ...options,
    },
  );

export const useGetCurrencies = (
  pageSize,
  pageNumber,
  isSupported,
  searchParam,
  options = {},
) =>
  useDataQuery(
    getCurrencies,
    "getCurrencies",
    [pageSize, pageNumber, isSupported, searchParam],
    {
      enabled: !!pageSize && !!pageNumber,
      ...options,
    },
  );

export const useGetAllDepts = (pageSize, pageNumber, options = {}) =>
  useDataQuery(getAllDepts, "getAllDepts", [pageSize, pageNumber], {
    enabled: !!pageSize && !!pageNumber,
    ...options,
  });

export const useAddStaff = (options) =>
  useDataMutation(addStaff, "addStaff", options);

export const useAddDept = (options) =>
  useDataMutation(addDept, "addDept", options);

export const useAddCountry = (options) =>
  useDataMutation(addCountry, "addCountry", options);

export const useAddCurrency = (options) =>
  useDataMutation(addCurrency, "addCurrency", options);

export const useAddLga = (options) =>
  useDataMutation(addLga, "addLga", options);

export const useAddState = (options) =>
  useDataMutation(addState, "addState", options);

export const useUpdateDept = (options) =>
  useDataMutation(updateDept, "updateDept", options);

export const useUpdateCountry = (options) =>
  useDataMutation(updateCountry, "updateCountry", options);

export const useUpdateCurrency = (options) =>
  useDataMutation(updateCurrency, "updateCurrency", options);

export const useUpdateState = (options) =>
  useDataMutation(updateState, "updateState", options);

export const useUpdateLga = (options) =>
  useDataMutation(updateLga, "updateLga", options);

export const useDeleteDept = (options) =>
  useDataMutation(deleteDept, "deleteDept", options);
